import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Center,
  Image,
  SimpleGrid,
  Stack,
  Text
} from "@chakra-ui/react";
import { FaBell, FaBuilding, FaShieldAlt, FaVideo } from "react-icons/fa";
import { api } from "../api";
import { useBranding } from "../context/BrandingContext";

const firstNameFrom = (value) => {
  const raw = String(value || "").trim();
  if (!raw) return "";
  const first = raw.split(/\s+/)[0];
  return first.charAt(0).toUpperCase() + first.slice(1).toLowerCase();
};

const FeatureCard = ({ title, subtitle, icon, onClick, tint }) => (
  <Button
    onClick={onClick}
    h="auto"
    minH={{ base: "150px", md: "212px" }}
    p={0}
    borderRadius={{ base: "16px", md: "22px" }}
    bg="rgba(17, 44, 30, 0.44)"
    border="1px solid rgba(241, 252, 244, 0.34)"
    backdropFilter="blur(6px)"
    boxShadow="0 12px 24px rgba(3, 18, 11, 0.2)"
    _hover={{ transform: "translateY(-1px)", bg: "rgba(18, 50, 33, 0.52)" }}
    _active={{ transform: "translateY(0)" }}
  >
    <Stack spacing={{ base: 1.5, md: 2.5 }} align="center" px={{ base: 1.5, md: 3 }} py={{ base: 2, md: 4 }} w="100%">
      <Box
        boxSize={{ base: "46px", md: "72px" }}
        borderRadius="14px"
        bg={tint}
        border="1px solid rgba(255,255,255,0.48)"
        display="flex"
        alignItems="center"
        justifyContent="center"
        boxShadow="0 8px 18px rgba(0,0,0,0.24)"
      >
        <Box as={icon} fontSize={{ base: "21px", md: "34px" }} color="#fffef2" />
      </Box>
      <Text
        color="#f4fbe9"
        fontWeight="700"
        fontSize={{ base: "11px", md: "xl" }}
        lineHeight="1.2"
        textAlign="center"
        noOfLines={{ base: 2, md: 3 }}
      >
        {title}
      </Text>
      <Text
        color="rgba(234, 245, 226, 0.9)"
        fontSize={{ base: "9px", md: "md" }}
        lineHeight="1.2"
        textAlign="center"
        noOfLines={{ base: 2, md: 4 }}
      >
        {subtitle}
      </Text>
    </Stack>
  </Button>
);

const Home = ({ onNavigate }) => {
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState(null);
  const [planos, setPlanos] = useState([]);

  const localUser = useMemo(() => JSON.parse(localStorage.getItem("user") || "{}"), []);
  const { branding, currentVisual, assetUrl } = useBranding();

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const [perfilRes, planosRes] = await Promise.all([
          api.get("/v2/usuarios/me"),
          api.get("/v2/planos", { params: { page: 1, page_size: 200 } })
        ]);
        setProfile(perfilRes.data);
        setPlanos(planosRes.data.items || []);
      } catch (err) {
        setProfile(localUser);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [localUser]);

  const planoAtual = useMemo(() => {
    if (!profile?.plano_id) return null;
    return planos.find((plano) => String(plano.id) === String(profile.plano_id)) || null;
  }, [planos, profile?.plano_id]);

  const vigenciaPlano = useMemo(() => {
    const ativacao = profile?.data_ativacao ? new Date(profile.data_ativacao) : null;
    const duracao = Number(profile?.duracao_plano || 0);
    if (!ativacao || Number.isNaN(ativacao.getTime())) return "Não informado";
    if (!duracao) return ativacao.toLocaleDateString("pt-BR");
    const fim = new Date(ativacao);
    fim.setMonth(fim.getMonth() + duracao);
    return fim.toLocaleDateString("pt-BR");
  }, [profile?.data_ativacao, profile?.duracao_plano]);

  const fullName = profile?.nome || localUser?.nome || "Eu Monitoro";
  const firstName = firstNameFrom(fullName) || "Usuário";
  const profileEmail = profile?.email || localUser?.email || "não informado";
  const planName = planoAtual?.nome || profile?.plano_nome || "Plano não informado";
  const backgroundUrl =
    assetUrl(currentVisual?.home_image_url) ||
    assetUrl(currentVisual?.background_image_url) ||
    assetUrl(branding.home_background_url) ||
    "/sistema/fundo.png";
  const logoUrl = assetUrl(branding.logo_url) || "/sistema/logo.png";

  return (
    <Box px={{ base: 0, md: 3 }} py={{ base: 0, md: 2 }}>
      <Box
        position="relative"
        minH={{ base: "calc(100vh - 172px)", md: "calc(100vh - 228px)" }}
        borderRadius={{ base: "0px", md: "30px" }}
        overflow="hidden"
        boxShadow={{ base: "none", md: "0 30px 80px rgba(5, 22, 13, 0.3)" }}
      >
        <Box
          position="absolute"
          inset="0"
          bgImage={`linear-gradient(180deg, rgba(13, 31, 17, 0.16), rgba(10, 26, 15, 0.72)), url('${backgroundUrl}')`}
          bgSize="cover"
          bgPosition="center"
        />
        <Box
          position="absolute"
          inset="0"
          bg="radial-gradient(circle at top right, rgba(239, 228, 152, 0.2), transparent 32%), radial-gradient(circle at bottom left, rgba(83, 192, 126, 0.2), transparent 40%)"
        />

        <Stack position="relative" zIndex="1" h="100%" spacing={{ base: 3, md: 5 }} p={{ base: 3, md: 7 }}>
          <Stack spacing={1.5} textAlign="center" align="center" pt={{ base: 1, md: 3 }}>
            <Text
              color="#f6ebc9"
              fontWeight="800"
              fontSize={{ base: "3xl", md: "5xl" }}
              lineHeight="1"
              textShadow="0 8px 26px rgba(0,0,0,0.4)"
              noOfLines={1}
            >
              Olá, {firstName}!
            </Text>
            <Text
              color="rgba(249, 255, 240, 0.96)"
              fontWeight="500"
              fontSize={{ base: "md", md: "2xl" }}
              maxW="900px"
              textShadow="0 8px 24px rgba(0,0,0,0.35)"
              lineHeight="1.25"
            >
              Bem-vindo ao <Text as="span" fontWeight="900">EU MONITORO</Text>, seu sistema de monitoramento inteligente.
            </Text>
          </Stack>

          <Center>
            <Box
              boxSize={{ base: "72px", md: "106px" }}
              borderRadius="full"
              bg="rgba(245, 255, 239, 0.14)"
              border="1px solid rgba(255,255,255,0.5)"
              display="flex"
              alignItems="center"
              justifyContent="center"
              backdropFilter="blur(8px)"
              boxShadow="0 18px 34px rgba(0,0,0,0.3)"
            >
              <Image src={logoUrl} alt="Logo" w={{ base: "44px", md: "68px" }} h={{ base: "44px", md: "68px" }} objectFit="contain" />
            </Box>
          </Center>

          <Box
            bg="rgba(243, 245, 230, 0.2)"
            border="1px solid rgba(245, 252, 230, 0.45)"
            borderRadius={{ base: "18px", md: "24px" }}
            px={{ base: 3, md: 5 }}
            py={{ base: 3, md: 4 }}
            backdropFilter="blur(10px)"
            boxShadow="0 18px 34px rgba(0,0,0,0.26)"
            color="#f4fbe9"
          >
            <Stack spacing={0.5}>
              <Text fontWeight="800" fontSize={{ base: "xl", md: "2xl" }}>
                Suas informações:
              </Text>
              <Text fontSize={{ base: "md", md: "xl" }} noOfLines={1}>
                <Text as="span" fontWeight="800">Nome:</Text> {firstName}
              </Text>
              <Text fontSize={{ base: "md", md: "xl" }} noOfLines={1}>
                <Text as="span" fontWeight="800">E-mail:</Text> {profileEmail}
              </Text>
              <Text fontSize={{ base: "md", md: "xl" }} noOfLines={1}>
                <Text as="span" fontWeight="800">Plano atual:</Text> {planName}
              </Text>
              <Text fontSize={{ base: "md", md: "xl" }} noOfLines={1}>
                <Text as="span" fontWeight="800">Vencimento:</Text> {vigenciaPlano}
              </Text>
            </Stack>
          </Box>

          <Text color="#f8f3dc" fontWeight="800" fontSize={{ base: "2xl", md: "4xl" }} textAlign="center" lineHeight="1.1">
            Recursos Principais
          </Text>

          <SimpleGrid columns={3} spacing={{ base: 2, md: 5 }}>
            <FeatureCard
              title="Monitoramento"
              subtitle="Ao vivo e DFI."
              icon={FaVideo}
              tint="linear-gradient(145deg, #8d948d, #5f655f)"
              onClick={() => onNavigate?.("mapa")}
            />
            <FeatureCard
              title="Alertas"
              subtitle="Gestão de alertas."
              icon={FaBell}
              tint="linear-gradient(145deg, #b3934a, #89652c)"
              onClick={() => onNavigate?.("eventos")}
            />
            <FeatureCard
              title="Comunidade"
              subtitle="Segurança comunitária."
              icon={FaShieldAlt}
              tint="linear-gradient(145deg, #92866e, #5f543f)"
              onClick={() => onNavigate?.("perfil")}
            />
          </SimpleGrid>

          <Center>
            <Button
              leftIcon={<FaBuilding />}
              borderRadius="14px"
              minH={{ base: "46px", md: "58px" }}
              px={{ base: 6, md: 9 }}
              fontSize={{ base: "lg", md: "xl" }}
              fontWeight="700"
              bg="linear-gradient(180deg, #f0f3f2, #c6cccb)"
              color="#1f2a2b"
              border="1px solid rgba(54, 70, 67, 0.28)"
              boxShadow="0 12px 22px rgba(0,0,0,0.24)"
              _hover={{ bg: "linear-gradient(180deg, #f6f7f6, #d0d6d5)" }}
              onClick={() => onNavigate?.("relatorios")}
            >
              Informações da empresa
            </Button>
          </Center>

          {loading && (
            <Text color="whiteAlpha.900" fontSize={{ base: "sm", md: "lg" }} textAlign="center">
              Carregando dados...
            </Text>
          )}
        </Stack>
      </Box>
    </Box>
  );
};

export default Home;

